'use client';
import React, { useMemo } from 'react';
import styled from '@benzinga/themetron';
import { Icon } from '@benzinga/core-ui';
import { faSearch, faXmark } from '@fortawesome/pro-regular-svg-icons';
import { GovernmentMember, SecuritiesSymbol } from '@benzinga/gov-trades';
import { TrackingManager } from '@benzinga/tracking-manager';
import { useBenzingaEdge } from '@benzinga/edge';
import { AiOutlineUnlock } from 'react-icons/ai';
import { SessionContext } from '@benzinga/session-context';

import { memberDisplayNameToProfileSlug } from './memberProfileSlug';
import { formatTickerToURLFriendly } from '@benzinga/utils';

export const congressParties = [
  { name: 'Democrat', party: 'D' },
  { name: 'Republican', party: 'R' },
  { name: 'Independent', party: 'I' },
];

const americanStates = [
  { name: 'Alaska', state: 'AK' },
  { name: 'Alabama', state: 'AL' },
  { name: 'Arkansas', state: 'AR' },
  { name: 'Arizona', state: 'AZ' },
  { name: 'California', state: 'CA' },
  { name: 'Colorado', state: 'CO' },
  { name: 'Connecticut', state: 'CT' },
  { name: 'Delaware', state: 'DE' },
  { name: 'Florida', state: 'FL' },
  { name: 'Georgia', state: 'GA' },
  { name: 'Hawaii', state: 'HI' },
  { name: 'Iowa', state: 'IA' },
  { name: 'Idaho', state: 'ID' },
  { name: 'Illinois', state: 'IL' },
  { name: 'Indiana', state: 'IN' },
  { name: 'Kansas', state: 'KS' },
  { name: 'Kentucky', state: 'KY' },
  { name: 'Louisiana', state: 'LA' },
  { name: 'Massachusetts', state: 'MA' },
  { name: 'Maryland', state: 'MD' },
  { name: 'Maine', state: 'ME' },
  { name: 'Michigan', state: 'MI' },
  { name: 'Minnesota', state: 'MN' },
  { name: 'Missouri', state: 'MO' },
  { name: 'Mississippi', state: 'MS' },
  { name: 'Montana', state: 'MT' },
  { name: 'North Carolina', state: 'NC' },
  { name: 'Nebraska', state: 'NE' },
  { name: 'New Hampshire', state: 'NH' },
  { name: 'New Jersey', state: 'NJ' },
  { name: 'New Mexico', state: 'NM' },
  { name: 'Nevada', state: 'NV' },
  { name: 'New York', state: 'NY' },
  { name: 'Ohio', state: 'OH' },
  { name: 'Oklahoma', state: 'OK' },
  { name: 'Oregon', state: 'OR' },
  { name: 'Pennsylvania', state: 'PA' },
  { name: 'Rhode Island', state: 'RI' },
  { name: 'South Carolina', state: 'SC' },
  { name: 'South Dakota', state: 'SD' },
  { name: 'Tennessee', state: 'TN' },
  { name: 'Texas', state: 'TX' },
  { name: 'Utah', state: 'UT' },
  { name: 'Virginia', state: 'VA' },
  { name: 'Vermont', state: 'VT' },
  { name: 'Washington', state: 'WA' },
  { name: 'Wisconsin', state: 'WI' },
  { name: 'West Virginia', state: 'WV' },
  { name: 'Wyoming', state: 'WY' },
];

const getFullStateName = (state: string) => {
  return americanStates.find(s => s.state === state)?.name.toLowerCase() ?? false;
};

const MemberSearchResult = ({ member }) => {
  const session = React.useContext(SessionContext);
  const { chamber, display_name, party, state } = member.filer_info;
  const parsedName = memberDisplayNameToProfileSlug(display_name);
  const partyLabel = congressParties.find(p => p.party === party)?.name || party;

  const trackSearchClick = () => {
    session.getManager(TrackingManager).trackSearchEvent('result_click', 'congress_member', {
      query: display_name,
    });
  };

  return (
    <a
      className="grid grid-cols-4 md:grid-cols-6 px-4 py-2 gap-4 hover:bg-bzblue-100 align-middle text-bzblue-900"
      href={`/government-trades/members/${parsedName}`}
      key={member.filer_info.id}
      onClick={trackSearchClick}
    >
      <div className="col-span-3 flex flex-row gap-4 items-center">
        <div className="whitespace-nowrap">{display_name}</div>
      </div>
      <div className="hidden md:flex items-center">{chamber}</div>
      <div className="hidden md:flex items-center">{partyLabel}</div>
      <div className="flex items-center">{state}</div>
    </a>
  );
};

const SecuritySearchResult = ({ security }) => {
  const { company_name, symbol, exchange, positions_active } = security;
  const session = React.useContext(SessionContext);

  const trackSearchClick = () => {
    session.getManager(TrackingManager).trackSearchEvent('result_click', 'security', {
      query: security.company_name,
    });
  };

  return (
    <a
      className="grid grid-cols-4 md:grid-cols-6 px-4 py-2 gap-4 hover:bg-bzblue-100 items-center text-bzblue-900"
      href={`/government-trades/securities/${formatTickerToURLFriendly(symbol)}`}
      key={symbol}
      onClick={trackSearchClick}
    >
      <div className="col-span-2 md:col-span-3">{company_name}</div>
      <div className="text-xs font-bold text-bzblue-800">
        {exchange}:({symbol})
      </div>
      <div className="text-lg font-bold text-bzblue-800">{positions_active}</div>
    </a>
  );
};

export const GovSearchBarV2 = ({
  members,
  securities,
}: {
  members: GovernmentMember[];
  securities: SecuritiesSymbol[];
}) => {
  const [search, setSearch] = React.useState<string>('');
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [membersResults, setMembersResults] = React.useState<GovernmentMember[]>([]);
  const [securitiesResults, setSecuritiesResults] = React.useState<SecuritiesSymbol[]>([]);
  const [locked, setLocked] = React.useState<boolean>(false);
  const placeholder = 'Search by Ticker, Politician, or Committee Chamber...';
  const unlockText = 'Unlock all features with Benzinga Edge';
  const { hasEdgeSubscription: hasPermission } = useBenzingaEdge();

  const membersFilter = useMemo(() => {
    if (!members || !search) return [];
    const filteredMembers = members?.filter(member => {
      const { chamber, display_name, state } = member.filer_info;
      const searchVal = search.toLowerCase();
      const stateName = getFullStateName(state);

      return (
        chamber.toLowerCase().includes(searchVal) ||
        display_name.toLowerCase().includes(searchVal) ||
        state.toLowerCase().includes(searchVal) ||
        (stateName ? stateName.includes(searchVal) : false)
      );
    });
    return filteredMembers;
  }, [members, search]);

  const securitiesFilter = useMemo(() => {
    if (!securities || !search) return [];
    const filtered = securities.filter(
      ticker =>
        ticker.company_name?.toLowerCase().startsWith(search.toLowerCase()) ||
        ticker.ticker?.startsWith(search.toUpperCase()),
    );
    return filtered;
  }, [securities, search]);

  React.useEffect(() => {
    if (!search) {
      setMembersResults([]);
      setSecuritiesResults([]);
    } else {
      const membersFiltered = membersFilter;
      const securitiesFiltered = securitiesFilter;
      setMembersResults(membersFiltered);
      setSecuritiesResults(securitiesFiltered);
    }
  }, [members, membersFilter, search, securitiesFilter]);

  React.useEffect(() => {
    if (hasPermission) {
      setLocked(false);
    } else {
      setLocked(true);
    }
  }, [hasPermission]);

  return (
    <SearchBarWrapper className="relative z-10 group">
      <div className="w-full searchbar">
        <Icon icon={faSearch} />
        <input
          className="w-full inner-search"
          disabled={locked}
          onChange={e => setSearch(e.target.value)}
          placeholder={placeholder}
          ref={inputRef}
          type="text"
          value={search}
        />
        {search && (
          <button
            aria-label="Clear search"
            className="flex items-center opacity-70 hover:opacity-100"
            onClick={() => {
              setSearch('');
              inputRef.current?.focus();
            }}
            type="button"
          >
            <Icon icon={faXmark} />
          </button>
        )}
      </div>
      {(membersResults.length > 0 || securitiesResults.length > 0) && (
        <div className="absolute max-h-96 overflow-auto w-full bg-white rounded-md shadow-md z-10 gov-search-results">
          {membersResults.length > 0 && (
            <div className="mb-4">
              <div className="grid grid-cols-4 md:grid-cols-6 gap-4 px-2 pt-2 gov-search-heading">
                <div className="col-span-3 text-sm font-bold text-bzblue-900/80">Congress Members</div>
                <div className="hidden md:flex text-sm font-bold text-bzblue-900/80">Chamber</div>
                <div className="hidden md:flex text-sm font-bold text-bzblue-900/80">Party</div>
                <div className="text-sm font-bold text-bzblue-900/80">State</div>
              </div>
              {membersResults.map(result => {
                return <MemberSearchResult key={result.filer_info.display_name} member={result} />;
              })}
            </div>
          )}
          {securitiesResults.length > 0 && (
            <div className="">
              <div className="grid grid-cols-4 md:grid-cols-6 gap-4 px-2 pt-2 gov-search-heading">
                <div className="col-span-2 md:col-span-3 text-sm font-bold text-bzblue-900/80">Securities</div>
                <div className="text-sm font-bold text-bzblue-900/80">Exchange</div>
                <div className="text-sm font-bold text-bzblue-900/80 whitespace-nowrap">Active Positions</div>
              </div>
              {securitiesResults.map(result => {
                return <SecuritySearchResult key={result.ticker} security={result} />;
              })}
            </div>
          )}
        </div>
      )}
      {membersResults.length === 0 && securitiesResults.length === 0 && search.length > 1 && (
        <div className="absolute max-h-96 overflow-scroll w-full bg-white rounded-md shadow-md z-10 gov-search-results">
          <div className="px-4 py-2 text-bzblue-900">No results found</div>
        </div>
      )}
      {locked && (
        <a
          className="z-20 absolute hidden group-hover:flex top-0 w-full h-full text-center items-center px-4 font-bold hover:text-bzblue-700 cursor-pointer backdrop-blur-sm"
          href="https://www.benzinga.com/premium/ideas/benzinga-edge-checkout/?t=be8be9we4gewe1be11&utm_source=government-trades"
          target="_blank"
        >
          <AiOutlineUnlock />
          {unlockText}
        </a>
      )}
    </SearchBarWrapper>
  );
};

const SearchBarWrapper = styled.div`
  .searchbar {
    padding: 8px 12px;
    gap: 16px;
    display: flex;
    border-radius: 8px;
    background: #f2f8ff;
    align-items: center;

    .inner-search {
      background: transparent;
    }
  }
`;
